import { Injectable } from '@angular/core';
import { MenuItem, LoginResponseData } from '../models';

@Injectable({ providedIn: 'root' })
export class MontarMenuService {
  /**
   * Monta o menu a partir dos sistemas disponíveis
   * Salva em localStorage para a sidebar usar
   */
  montarMenuAPartirDosistemas(usuario: LoginResponseData): void {
    try {
      if (!usuario?.sistemas || !Array.isArray(usuario.sistemas)) return;

      const norm = (p: string) => (p || '').trim().replace(/\/+/g, '/');
      const strip = (p: string) => norm(p).replace(/^\/|\/$/g, '');
      const abs = (p: string) => (p.startsWith('/') ? norm(p) : `/${norm(p)}`);
      const join = (...parts: string[]) => {
        const full = parts
          .filter(Boolean)
          .map((x) => strip(x))
          .filter(Boolean)
          .join('/');
        return `/${full}`.replace(/\/+/g, '/');
      };
      const isRotaClicavel = (rota: string) =>
        !String(rota || '').includes('/:');
      const resolveFrontPath = (
        modulo: any,
        componente?: any,
        funcao?: any
      ) => {
        const moduloBase = strip(modulo?.caminho_exibicao || '');
        const componenteBase = strip(componente?.caminho_exibicao || '');

        const rotaFuncao = String(funcao?.rota || '').trim();
        if (rotaFuncao) {
          const rotaAbs = abs(rotaFuncao);
          if (moduloBase && rotaAbs.startsWith(`/${moduloBase}`))
            return rotaAbs;
          if (moduloBase) return join(moduloBase, rotaAbs);
          return rotaAbs;
        }

        if (moduloBase && componenteBase) {
          if (componenteBase === moduloBase) return `/${moduloBase}`;
          return join(moduloBase, componenteBase);
        }

        if (moduloBase) return join(moduloBase);
        return '/modulos';
      };

      let menuItems: MenuItem[] = [];
      usuario.sistemas.forEach((sistema: any) => {
        (sistema?.modulos || []).forEach((modulo: any) => {
          const componentes = Array.isArray(modulo?.componentes)
            ? modulo.componentes
            : [];

          const subItems: any[] = [];
          componentes.forEach((componente: any) => {
            const funcoes = Array.isArray(componente?.funcoes)
              ? componente.funcoes
              : [];

            if (funcoes.length) {
              const countAntes = subItems.length;
              funcoes.forEach((funcao: any) => {
                const rota = String(funcao?.rota || '').trim();
                if (!rota) return;
                if (!isRotaClicavel(rota)) return;
                subItems.push({
                  titulo: this.formatarTitulo(funcao?.nome || 'Opção'),
                  path: resolveFrontPath(modulo, componente, funcao),
                });
              });
              // fallback: se nenhuma função gerou item clicável, usa o componente
              if (subItems.length === countAntes) {
                subItems.push({
                  titulo: this.formatarTitulo(componente?.nome || 'Componente'),
                  path: resolveFrontPath(modulo, componente),
                });
              }
              return;
            }

            subItems.push({
              titulo: this.formatarTitulo(componente?.nome || 'Componente'),
              path: resolveFrontPath(modulo, componente),
            });
          });

          const defaultPath =
            subItems[0]?.path || resolveFrontPath(modulo, componentes?.[0]);

          menuItems.push({
            icon: `${modulo?.icone || 'icon-default'}.png`,
            titulo: String(modulo?.nome || 'Módulo'),
            path: defaultPath,
            subItems,
            moduloCompleto: modulo,
          });
        });
      });

      // Filtrar itens de menu que terminam com '/criar' (agora são modais)
      menuItems = this.filtrarItensDeMenu(menuItems);

      localStorage.setItem('menu_sidebar', JSON.stringify(menuItems));
    } catch {
      // silencia erros para não quebrar login
    }
  }

  private filtrarItensDeMenu(items: any[]): any[] {
    return items
      .map((item: any) => {
        // Filtrar subitens
        if (item.subItems && item.subItems.length > 0) {
          item.subItems = item.subItems.filter((subItem: any) => {
            // Remover itens que terminam com "/criar"
            return !subItem.path?.endsWith('/criar');
          });
        }
        return item;
      })
      .filter((item: any) => {
        // Remover item pai se não tiver mais subitens (opcional)
        if (item.subItems && item.subItems.length === 0 && !item.path) {
          return false;
        }
        // Remover item se a própria URL termina com "/criar"
        return !item.path?.endsWith('/criar');
      });
  }

  private formatarTitulo(nome: string): string {
    return (nome || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }
}
